import { useEffect, useRef, useState } from "react";
import { Lightbulb, Sparkles, Bookmark, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSystemActivity } from "@/hooks/useSystemActivity";
import { fetchers, qk } from "@/lib/queryClient";
import { usePriorityBackbone } from "@/hooks/usePriorityBackbone";
import StrategicOverview from "@/components/StrategicOverview";
import {
  EditorialGridSkeleton,
  EditorialKpisSkeleton,
} from "@/components/PageSkeletons";

const STATUS_TABS = [
  { key: "pending",   label: "Pending" },
  { key: "saved",     label: "Saved" },
  { key: "generated", label: "Generated" },
  { key: "dismissed", label: "Dismissed" },
];

function Chip({ children, priority = false }) {
  // Phase 7 — chips are backbone-priority-aware. Sky-blue when the tag
  // matches a priority company/topic/market, neutral otherwise.
  const styles = priority
    ? {
        background: "#E6F4FB",
        color: "#0FA9DB",
        border: "1px solid #B8E1F3",
        fontWeight: 600,
      }
    : {
        background: "#F7FAFC",
        color: "#3D4B62",
        border: "1px solid #E5E9F0",
        fontWeight: 500,
      };
  return (
    <span
      className="inline-block rounded-full whitespace-nowrap"
      style={{ ...styles, fontSize: 12, padding: "3px 10px", letterSpacing: "0.1px" }}
      title={priority ? "Priority backbone entity" : undefined}
      data-testid={priority ? "chip-priority" : "chip-default"}
    >
      {children}
    </span>
  );
}

function ProposalCard({ p, onPatch, onGenerate, generatingId, onOpen, fresh, isPriority }) {
  const conf = Math.round((p.confidence || 0) * 100);
  return (
    <div
      className={`hm-surface p-5 flex flex-col gap-3${fresh ? " hm-fresh-proposal" : ""}`}
      data-testid={`proposal-card-${p.id}`}
    >
      <div className="flex items-start gap-3">
        <Lightbulb
          className="shrink-0"
          style={{ width: 22, height: 22, color: "var(--hm-accent)" }}
          strokeWidth={1.8}
        />
        <div className="flex-1 min-w-0">
          <h3
            className="text-hm-fg"
            style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}
          >
            {p.title_proposed}
          </h3>
          <p
            className="text-hm-fg-muted mt-2"
            style={{ fontSize: 14, fontStyle: "italic", lineHeight: 1.5 }}
          >
            {p.thesis}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mono" style={{ fontSize: 12 }}>
        <span className="text-hm-fg" style={{ fontWeight: 600 }}>
          conf {conf}%
        </span>
        <span className="text-hm-fg-subtle">·</span>
        <span className="text-hm-fg-muted">{p.source_count} sources</span>
        {p.angle && (
          <>
            <span className="text-hm-fg-subtle">·</span>
            <span className="text-hm-fg-muted uppercase tracking-widest" style={{ fontSize: 10 }}>
              {p.angle}
            </span>
          </>
        )}
        {/* Turn D · RAG state badges */}
        {p.stale && (
          <span
            data-testid={`proposal-stale-${p.id}`}
            title="Stale: new evidence linked since this proposal was drafted"
            style={{ background: "#E6F4FB", color: "#0FA9DB", border: "1px solid #B8E1F3",
                     fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                     padding: "2px 7px", borderRadius: 999 }}
          >STALE</span>
        )}
        {p.review_status && p.review_status !== "ok" && (
          <span
            data-testid={`proposal-review-${p.id}`}
            title={`Review status: ${p.review_status}`}
            style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A",
                     fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                     padding: "2px 7px", borderRadius: 999 }}
          >{p.review_status === "possible_duplicate" ? "POSSIBLE DUP" : "REVIEW NEEDED"}</span>
        )}
        {p.alerts_count > 0 && (
          <span
            data-testid={`proposal-alerts-${p.id}`}
            title={`${p.alerts_count} new-evidence alert${p.alerts_count === 1 ? "" : "s"} on this published proposal`}
            style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A",
                     fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                     padding: "2px 7px", borderRadius: 999 }}
          >⚠ NEW EVIDENCE ({p.alerts_count})</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {p.companies_involved.slice(0, 6).map((c) => (
          <Chip key={`c-${c}`} priority={isPriority(c)}>{c}</Chip>
        ))}
        {p.topics_involved.slice(0, 5).map((t) => (
          <Chip key={`t-${t}`} priority={isPriority(t)}>{t}</Chip>
        ))}
        {p.regions_involved.map((r) => (
          <Chip key={`r-${r}`} priority={isPriority(r)}>{r.replace("_", " ")}</Chip>
        ))}
      </div>

      <div className="mono text-hm-fg-subtle" style={{ fontSize: 11 }}>
        {new Date(p.created_at).toLocaleString("en-GB", {
          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        })}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-hm-border">
        {p.has_generated ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpen(p.id)}
            data-testid={`proposal-open-${p.id}`}
          >
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Read article
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => onGenerate(p.id)}
            disabled={generatingId === p.id}
            data-testid={`proposal-generate-${p.id}`}
            style={{ background: "var(--hm-accent)", color: "#fff" }}
          >
            {generatingId === p.id ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Writing…</>
            ) : (
              <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate article</>
            )}
          </Button>
        )}
        {p.status !== "saved" && (
          <Button size="sm" variant="ghost" onClick={() => onPatch(p.id, "saved")}>
            <Bookmark className="h-3.5 w-3.5 mr-1.5" />
            Save
          </Button>
        )}
        <div className="flex-1" />
        <button
          onClick={() => onPatch(p.id, "dismissed")}
          className="text-hm-fg-subtle hover:text-hm-danger transition-colors"
          title="Dismiss"
          data-testid={`proposal-dismiss-${p.id}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ReadDrawer({ proposal, onClose }) {
  if (!proposal) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(15,27,45,0.35)" }}
      onClick={onClose}
    >
      <div
        className="bg-hm-bg h-full overflow-y-auto shadow-2xl p-8"
        style={{ width: "min(720px, 92vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-hm-fg" style={{ fontSize: 22, fontWeight: 600 }}>
            {proposal.title_proposed}
          </h2>
          <button onClick={onClose} className="text-hm-fg-subtle hover:text-hm-fg">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-hm-fg-muted italic mb-6" style={{ fontSize: 14 }}>
          {proposal.thesis}
        </p>
        <article
          className="text-hm-fg whitespace-pre-wrap"
          style={{ fontSize: 15, lineHeight: 1.7 }}
          data-testid="proposal-article-body"
        >
          {proposal.generated_content}
        </article>
      </div>
    </div>
  );
}

export default function Editorial() {
  const [tab, setTab] = useState("pending");
  const [minConf, setMinConf] = useState(0.0);
  const [generatingId, setGeneratingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openContent, setOpenContent] = useState(null);
  const [editorialActive, setEditorialActive] = useState(null);
  // Ids of freshly optimistically-inserted proposals — used to trigger the
  // sky-blue halo animation on the new card. Halo lifetime = 2.6s (matches
  // hm-fresh-proposal keyframes).
  const [freshIds, setFreshIds] = useState(new Set());
  // Optimistic patch: cards inserted BEFORE the real fetch has run.
  // Merged in front of `data.data` when rendering. Once a real refetch
  // includes them (by id), they are dropped from this local buffer.
  const [optimistic, setOptimistic] = useState([]);
  const seenOptimisticRef = useRef(new Set());
  const activity = useSystemActivity();
  const queryClient = useQueryClient();
  const { has: isPriority } = usePriorityBackbone();

  const queryParams = { status: tab, minConf, limit: 50 };
  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: qk.editorialProposals(queryParams),
    queryFn: () => fetchers.editorialProposals(queryParams),
    placeholderData: keepPreviousData,
  });

  const items = data?.data || [];
  const meta = data?.meta || { pending: 0, saved: 0, generated: 0, avg_confidence_pending: 0 };

  // Reconcile optimistic → server on every refetch that returns a fresh row.
  useEffect(() => {
    if (!data?.data?.length) return;
    const serverIds = new Set(data.data.map((p) => p.id));
    setOptimistic((prev) => prev.filter((p) => !serverIds.has(p.id)));
  }, [data]);

  // Reset optimistic buffer when the user switches tabs — a proposal
  // inserted optimistically into "pending" should not follow to "saved".
  useEffect(() => {
    setOptimistic([]);
    seenOptimisticRef.current = new Set();
  }, [tab]);

  // React to editorial activity events (from _run_editorial_posthooks).
  // Two responsibilities:
  //   1. per-proposal → optimistic insert + halo (see next effect)
  //   2. run-level (started/signals_done/completed/failed) → toast + refetch
  useEffect(() => {
    const evts = activity?.events || [];
    const latest = evts.find((e) =>
      ["editorial_run_started", "editorial_signals_done", "editorial_run_completed", "editorial_run_failed"].includes(e.kind)
    );
    if (!latest) return;
    if (latest.kind === "editorial_run_completed") {
      const n = latest.meta?.proposals_created || 0;
      if (n > 0) {
        toast.success(`${n} new editorial proposal${n === 1 ? "" : "s"} available`, {
          action: { label: "See them", onClick: () => setTab("pending") },
        });
      }
      setEditorialActive(null);
      // Silent revalidation — no spinner, cache is fresh already.
      queryClient.invalidateQueries({ queryKey: ["editorial", "proposals"] });
    } else if (latest.kind === "editorial_run_failed") {
      setEditorialActive(null);
    } else {
      setEditorialActive(latest);
    }
  }, [activity?.events, queryClient]);

  // Optimistic insertion on every editorial_proposal_created event.
  // The backend now emits the FULL proposal row inside e.proposal, so we can
  // render the card immediately with zero fetch. Fallback: if e.proposal
  // is missing (older payloads), mono-fetch by id to hydrate.
  useEffect(() => {
    const evts = activity?.events || [];
    // Oldest first so the resulting `optimistic` array keeps insert order.
    for (let i = evts.length - 1; i >= 0; i--) {
      const e = evts[i];
      if (e?.kind !== "editorial_proposal_created") continue;
      const p = e.proposal;
      if (!p?.id) continue;
      if (seenOptimisticRef.current.has(p.id)) continue;
      seenOptimisticRef.current.add(p.id);
      // Only surface in "pending" tab (that's where fresh proposals land).
      if (tab !== "pending") continue;
      // Respect the confidence filter — silent drop, not a UI decision.
      if ((p.confidence || 0) < minConf) continue;

      setOptimistic((prev) => {
        if (prev.some((x) => x.id === p.id)) return prev;
        return [p, ...prev];
      });
      setFreshIds((prev) => {
        const s = new Set(prev);
        s.add(p.id);
        return s;
      });
      // Halo lifetime matches the keyframes (2.6s) — remove the class after
      // so React can re-render without re-triggering the animation.
      setTimeout(() => {
        setFreshIds((prev) => {
          if (!prev.has(p.id)) return prev;
          const s = new Set(prev);
          s.delete(p.id);
          return s;
        });
      }, 2800);
    }
  }, [activity?.events, tab, minConf]);

  const onPatch = async (id, status) => {
    await fetch(`${API}/editorial/proposals/${id}`, {
      method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    // Invalidate every editorial list — tabs may have changed too.
    queryClient.invalidateQueries({ queryKey: ["editorial", "proposals"] });
  };

  const onGenerate = async (id) => {
    setGeneratingId(id);
    try {
      const r = await fetch(`${API}/editorial/proposals/${id}/generate`, {
        method: "POST", headers: authHeaders(),
      });
      if (r.ok) queryClient.invalidateQueries({ queryKey: ["editorial", "proposals"] });
    } finally {
      setGeneratingId(null);
    }
  };

  const onOpen = async (id) => {
    const r = await fetch(`${API}/editorial/proposals/${id}`, { headers: authHeaders() });
    if (r.ok) {
      const p = await r.json();
      setOpenContent(p);
      setOpenId(id);
    }
  };

  // Merged render list — optimistic items in front, deduped by id so a late
  // refetch that already includes an optimistic proposal doesn't render it twice.
  const merged = (() => {
    if (!optimistic.length) return items;
    const serverIds = new Set(items.map((p) => p.id));
    const uniquePending = optimistic.filter((p) => !serverIds.has(p.id));
    return [...uniquePending, ...items];
  })();

  const topSignal = merged[0]?.companies_involved?.[0]
    || merged[0]?.topics_involved?.[0]
    || "—";

  return (
    <div data-testid="editorial-page">
      <PageHeader
        title="Editorial"
        subtitle="Original angles generated from cross-article signals."
      />
      {/* Sprint 1 · Turn B — Strategic overview (silent if backend has none) */}
      <StrategicOverview />
      {/* KPI header — skeleton while pending, real values as soon as cache warms */}
      {isPending || !data ? (
        <EditorialKpisSkeleton />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="editorial-kpis">
          <div className="hm-surface p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">Pending</div>
            <div className="text-hm-fg mt-1" style={{ fontSize: 28, fontWeight: 600 }}>{meta.pending}</div>
          </div>
          <div className="hm-surface p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">Avg confidence</div>
            <div className="text-hm-fg mt-1" style={{ fontSize: 28, fontWeight: 600 }}>
              {meta.avg_confidence_pending?.toFixed(2)}
            </div>
          </div>
          <div className="hm-surface p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">Generated</div>
            <div className="text-hm-fg mt-1" style={{ fontSize: 28, fontWeight: 600 }}>{meta.generated}</div>
          </div>
          <div className="hm-surface p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">Top signal</div>
            <div className="text-hm-fg mt-1 truncate" style={{ fontSize: 20, fontWeight: 600 }}>{topSignal}</div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex items-center gap-4 mb-4 border-b border-hm-border">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`editorial-tab-${t.key}`}
            className={
              "px-1 py-3 mono uppercase tracking-widest transition-colors " +
              (tab === t.key
                ? "text-hm-fg border-b-2 border-hm-accent"
                : "text-hm-fg-subtle hover:text-hm-fg")
            }
            style={{ fontSize: 11 }}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="flex items-center gap-3 mono text-hm-fg-subtle" style={{ fontSize: 11 }}>
          <span>min conf</span>
          <input
            type="range" min="0" max="0.99" step="0.05"
            value={minConf} onChange={(e) => setMinConf(parseFloat(e.target.value))}
            className="w-32"
          />
          <span className="text-hm-fg">{minConf.toFixed(2)}</span>
        </div>
      </div>

      {isPending || !data ? (
        <EditorialGridSkeleton n={6} />
      ) : merged.length === 0 ? (
        <div className="text-center py-16 text-hm-fg-subtle mono uppercase tracking-widest" style={{ fontSize: 12 }}>
          No proposals in this bucket
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="editorial-grid">
          {merged.map((p) => (
            <ProposalCard
              key={p.id} p={p}
              onPatch={onPatch}
              onGenerate={onGenerate}
              generatingId={generatingId}
              onOpen={onOpen}
              fresh={freshIds.has(p.id)}
              isPriority={isPriority}
            />
          ))}
        </div>
      )}

      {openId && (
        <ReadDrawer proposal={openContent} onClose={() => { setOpenId(null); setOpenContent(null); }} />
      )}
    </div>
  );
}
